import { useEffect, useMemo, useState } from 'react';

/**
 * Seamless flat-top honeycomb — full hexes (no pattern clipping),
 * neon borders matching login form + soft glow paths + particles.
 */

const ROOT3 = Math.sqrt(3);
const HEX_W = 70;
const R = HEX_W / 2;
const STEP_X = HEX_W * 0.75;
const STEP_Y = ROOT3 * R;
const FILL_R = R + 0.55;

function flatHexPoints(cx, cy, radius) {
  return [0, 1, 2, 3, 4, 5]
    .map((i) => {
      const a = (Math.PI / 180) * (60 * i);
      return `${cx + radius * Math.cos(a)},${cy + radius * Math.sin(a)}`;
    })
    .join(' ');
}

function hexCenter(col, row) {
  return {
    x: col * STEP_X + R,
    y: row * STEP_Y + (col % 2) * (STEP_Y / 2) + STEP_Y / 2,
  };
}

function seededRand(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function buildViewportGrid(width, height) {
  const padX = STEP_X * 2;
  const padY = STEP_Y * 2;
  const cols = Math.ceil((width + padX * 2) / STEP_X) + 1;
  const rows = Math.ceil((height + padY * 2) / STEP_Y) + 1;
  const cells = [];

  for (let col = 0; col < cols; col += 1) {
    for (let row = 0; row < rows; row += 1) {
      const { x, y } = hexCenter(col, row);
      cells.push({
        key: `${col}-${row}`,
        fill: flatHexPoints(x, y, FILL_R),
        border: flatHexPoints(x, y, R),
      });
    }
  }

  return cells;
}

function buildGlowPaths() {
  const rand = seededRand(0x94ff33);
  const paths = [];
  const count = 5;

  for (let p = 0; p < count; p += 1) {
    const angle = (p / count) * Math.PI * 2 + rand() * 0.35;
    let x = 50;
    let y = 48;
    const parts = [`M ${x.toFixed(2)} ${y.toFixed(2)}`];
    const steps = 7 + Math.floor(rand() * 3);

    for (let s = 0; s < steps; s += 1) {
      const spread = 4 + s * 2.1;
      const a = angle + (rand() - 0.5) * 0.4;
      x += Math.cos(a) * spread;
      y += Math.sin(a) * spread;
      parts.push(`L ${x.toFixed(2)} ${y.toFixed(2)}`);
      if (rand() < 0.3) {
        const bx = x + Math.cos(a + 0.85) * (3 + rand() * 3.5);
        const by = y + Math.sin(a + 0.85) * (3 + rand() * 3.5);
        parts.push(`M ${x.toFixed(2)} ${y.toFixed(2)} L ${bx.toFixed(2)} ${by.toFixed(2)}`);
        parts.push(`M ${x.toFixed(2)} ${y.toFixed(2)}`);
      }
    }

    paths.push({
      d: parts.join(' '),
      delay: `${(p * 0.35 + rand() * 0.35).toFixed(2)}s`,
      duration: `${9 + rand() * 3}s`,
    });
  }

  return paths;
}

function buildParticles(count) {
  const rand = seededRand(0xc0ffee);
  return Array.from({ length: count }, (_, i) => {
    const tiny = rand() < 0.62;
    return {
      id: i,
      left: `${rand() * 100}%`,
      top: `${rand() * 100}%`,
      size: tiny ? 1.2 + rand() * 1.4 : 2 + rand() * 2.2,
      delay: `${(rand() * 10).toFixed(2)}s`,
      duration: `${18 + rand() * 14}s`,
      dx: `${((rand() - 0.5) * 32).toFixed(1)}px`,
      dy: `${(-10 - rand() * 36).toFixed(1)}px`,
      tiny,
    };
  });
}

const GLOW_PATHS = buildGlowPaths();
const PARTICLES = buildParticles(28);

function AuthHexBackground() {
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));

  useEffect(() => {
    let timer = 0;
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        setViewport({ w: window.innerWidth, h: window.innerHeight });
      }, 150);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const cells = useMemo(
    () => buildViewportGrid(viewport.w, viewport.h),
    [viewport.w, viewport.h],
  );

  return (
    <div className="auth-hex-bg" aria-hidden="true">
      <svg
        className="auth-hex-svg"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
      >
        <defs>
          <linearGradient id="auth-hex-face-grad" x1="20%" y1="0%" x2="80%" y2="100%">
            <stop offset="0%" className="auth-hex-face-hi" />
            <stop offset="55%" className="auth-hex-face-mid" />
            <stop offset="100%" className="auth-hex-face-lo" />
          </linearGradient>
          <filter id="auth-hex-border-glow" x="-70%" y="-70%" width="240%" height="240%">
            <feGaussianBlur stdDeviation="1.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="auth-hex-fog" cx="50%" cy="48%" r="58%">
            <stop offset="0%" className="auth-hex-fog-stop-core" />
            <stop offset="50%" className="auth-hex-fog-stop-mid" />
            <stop offset="100%" className="auth-hex-fog-stop-edge" />
          </radialGradient>
        </defs>

        <rect width="100%" height="100%" className="auth-hex-underlay" />

        <g className="auth-hex-grid">
          {cells.map((cell) => (
            <polygon
              key={`f-${cell.key}`}
              points={cell.fill}
              className="auth-hex-cell"
              fill="url(#auth-hex-face-grad)"
            />
          ))}
        </g>

        <g className="auth-hex-border-layer" filter="url(#auth-hex-border-glow)">
          {cells.map((cell) => (
            <polygon
              key={`b-${cell.key}`}
              points={cell.border}
              className="auth-hex-border"
              fill="none"
            />
          ))}
        </g>

        <rect width="100%" height="100%" fill="url(#auth-hex-fog)" className="auth-hex-fog" />
      </svg>

      <svg
        className="auth-hex-glow-svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {GLOW_PATHS.map((path, i) => (
          <path
            key={i}
            d={path.d}
            className="auth-hex-glow-path"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            style={{
              '--glow-delay': path.delay,
              '--glow-duration': path.duration,
            }}
          />
        ))}
      </svg>

      <div className="auth-hex-particles">
        {PARTICLES.map((p) => (
          <span
            key={p.id}
            className={p.tiny ? 'auth-hex-particle auth-hex-particle-tiny' : 'auth-hex-particle'}
            style={{
              left: p.left,
              top: p.top,
              width: p.size,
              height: p.size,
              '--p-delay': p.delay,
              '--p-duration': p.duration,
              '--p-dx': p.dx,
              '--p-dy': p.dy,
            }}
          />
        ))}
      </div>

      <div className="auth-hex-vignette" />
    </div>
  );
}

export default AuthHexBackground;
